export const M1_UNIT_5 = {
  id: "u5-things-you-have",
  title: "Unit 5: Ownership",
  topic: "Possessive Adjectives & Demonstratives",
  iconType: "tag",
  grammarImage: "/u5-possessives.jpg",
  grammarText: "Use of 'My, Your, His, Her, Its, Our, Their' for possession. Difference between This (near) and That (far).",
  
  sets: [
    // ===================================================================
    // SET 1: MY / YOUR (A1.0)
    // ===================================================================
    {
      id: "u5-set1",
      title: "Set 1: My Things",
      context: `*Primer Día de Clase - Madrid*

Emma es profesora de inglés en Madrid.
Ella presenta sus cosas: "This is my book. This is my pen."
Los estudiantes preguntan: "Is this your bag?"

*First Day of Class - Madrid*

Emma is an English teacher in Madrid.
She presents her things: "This is my book. This is my pen."
Students ask: "Is this your bag?"`,
      
      questions: [
        {
          type: "match",
          q: "Match each subject to its possessive adjective.",
          pairs: [
            { left: "I have a cat", right: "My cat" },
            { left: "You have a dog", right: "Your dog" },
            { left: "I have a book", right: "My book" }
          ],
          a: "completed",
          tip: "I → My, You → Your.",
          successTip: "Possessive adjectives! I → MY (mi/mis). You → YOUR (tu/tus). Always before the noun! 📚"
        },
        
        {
          type: "mc",
          q: "Emma: 'I have a car. It is ___ car.'",
          options: ["my", "your", "his"],
          a: "my",
          tip: "I → my.",
          successTip: "'I have' → 'my'! I have a car = My car. Pattern! 🚗"
        },
        
        {
          type: "mc",
          q: "Emma asks student: 'You have a phone. It is ___ phone.'",
          options: ["your", "my", "her"],
          a: "your",
          tip: "You → your."
        },
        
        {
          type: "input",
          q: "Emma: 'I love ___ family.'",
          a: "my",
          alts: [],
          tip: "I → my (mi familia)."
        },
        
        {
          type: "input",
          q: "Emma asks: 'Is this ___ pen?'",
          a: "your",
          alts: [],
          tip: "You → your (tu bolígrafo).",
          successTip: "'Is this your...?' = common question! Is this your phone/pen/book? Use it! ❓"
        },
        
        {
          type: "order",
          q: "Traduce: 'Este es mi perro.'",
          words: ["This", "is", "my", "dog."],
          a: "This is my dog.",
          extra: ["your", "cat"],
          tip: "'My' = mi/mis."
        },
        
        {
          type: "match",
          q: "Match each subject pronoun to possessive adjective.",
          pairs: [
            { left: "I", right: "my" },
            { left: "You", right: "your" },
            { left: "We", right: "our" }
          ],
          a: "completed",
          tip: "Pronouns change for possession.",
          successTip: "Pattern! I → MY, You → YOUR, We → OUR. Memorize these pairs! 📝"
        },
        
        {
          type: "mc",
          q: "Student: '___ name is Carlos.'",
          options: ["My", "Your", "His"],
          a: "My",
          tip: "Student talks about himself = My."
        },
        
        {
          type: "input",
          q: "Emma asks student: 'What is ___ name?'",
          a: "your",
          alts: [],
          tip: "Asking 'you' = your.",
          successTip: "'What is your name?' = THE first question in English! Learn it! 💬"
        },
        
        {
          type: "order",
          q: "Traduce: 'Mi gato es negro.'",
          words: ["My", "cat", "is", "black."],
          a: "My cat is black.",
          extra: ["dog", "white"],
          tip: "'My cat' = mi gato."
        }
      ]
    },

    // ===================================================================
    // SET 2: HIS / HER (A1.0)
    // ===================================================================
    {
      id: "u5-set2",
      title: "Set 2: Family Members",
      context: `*Mostrando Fotos - Clase*

Emma muestra fotos de su familia.
"This is my brother. His name is Tom. This is my sister. Her name is Lisa."
Los estudiantes practican: "His/Her".

*Showing Photos - Class*

Emma shows photos of her family.
"This is my brother. His name is Tom. This is my sister. Her name is Lisa."
Students practice: "His/Her".`,
      
      questions: [
        {
          type: "match",
          q: "Match each person to their possessive adjective.",
          pairs: [
            { left: "He has a dog", right: "His dog" },
            { left: "She has a cat", right: "Her cat" },
            { left: "John has a phone", right: "His phone" }
          ],
          a: "completed",
          tip: "He → His, She → Her.",
          successTip: "Gender matters! He (male) → HIS. She (female) → HER. Important difference! ♂️♀️"
        },
        
        {
          type: "mc",
          q: "Emma: 'This is Maria. ___ book is red.'",
          options: ["Her", "His", "My"],
          a: "Her",
          tip: "Maria (female) → Her.",
          successTip: "Female name = HER! Maria/Lisa/Anna → HER. Always! 👩"
        },
        
        {
          type: "mc",
          q: "Emma: 'This is John. ___ car is blue.'",
          options: ["His", "Her", "Your"],
          a: "His",
          tip: "John (male) → His."
        },
        
        {
          type: "input",
          q: "Emma: 'This is my brother. ___ name is Tom.'",
          a: "His",
          alts: ["his"],
          tip: "Brother (male) → His."
        },
        
        {
          type: "input",
          q: "Emma: 'This is my sister. ___ name is Lisa.'",
          a: "Her",
          alts: ["her"],
          tip: "Sister (female) → Her.",
          successTip: "Brother → HIS. Sister → HER. Don't confuse! Very common mistake! ⚠️"
        },
        
        {
          type: "order",
          q: "Traduce: 'Su gato es lindo.' (de ella)",
          words: ["Her", "cat", "is", "cute."],
          a: "Her cat is cute.",
          extra: ["His", "dog"],
          tip: "'Her' = su (de ella)."
        },
        
        {
          type: "match",
          q: "Match male/female to possessive.",
          pairs: [
            { left: "A man", right: "his" },
            { left: "A woman", right: "her" },
            { left: "Tom", right: "his" }
          ],
          a: "completed",
          tip: "Man/boy → his, Woman/girl → her.",
          successTip: "Easy rule! Male names/man/boy → HIS. Female names/woman/girl → HER. 👨👩"
        },
        
        {
          type: "mc",
          q: "Emma: '___ father is 60.' (talking about herself)",
          options: ["My", "Her", "His"],
          a: "My",
          tip: "Emma talks about HER OWN father = My."
        },
        
        {
          type: "input",
          q: "Emma points to Tom: '___ dog is big.'",
          a: "His",
          alts: ["his"],
          tip: "Tom (male) → His."
        },
        
        {
          type: "order",
          q: "Traduce: 'Su coche es azul.' (de él)",
          words: ["His", "car", "is", "blue."],
          a: "His car is blue.",
          extra: ["Her", "red"],
          tip: "'His' = su (de él)."
        }
      ]
    },

    // ===================================================================
    // SET 3: OUR / THEIR / ITS (A1+)
    // ===================================================================
    {
      id: "u5-set3",
      title: "Set 3: Groups and Animals",
      context: `*Hablando de Grupos - Clase*

Emma habla de grupos: "We have a school. Our school is big."
"They have a house. Their house is small."
"The dog has a ball. Its ball is red."

*Talking About Groups - Class*

Emma talks about groups: "We have a school. Our school is big."
"They have a house. Their house is small."
"The dog has a ball. Its ball is red."`,
      
      questions: [
        {
          type: "match",
          q: "Match each subject to its possessive adjective.",
          pairs: [
            { left: "We have a house", right: "Our house" },
            { left: "They have a car", right: "Their car" },
            { left: "The dog has a toy", right: "Its toy" }
          ],
          a: "completed",
          tip: "We → Our, They → Their, It → Its.",
          successTip: "Plurals! We → OUR (nuestro/a). They → THEIR (su de ellos). It → ITS (su de cosa/animal). 👥"
        },
        
        {
          type: "mc",
          q: "Emma: 'We are students. ___ school is big.'",
          options: ["Our", "Their", "My"],
          a: "Our",
          tip: "We → Our.",
          successTip: "'Our' = nuestro/nuestra/nuestros/nuestras! One word for all! 🏫"
        },
        
        {
          type: "mc",
          q: "Emma: 'My parents are 60. ___ house is old.'",
          options: ["Their", "Our", "His"],
          a: "Their",
          tip: "Parents (they) → Their."
        },
        
        {
          type: "input",
          q: "Emma: 'The cat has a toy. ___ toy is small.'",
          a: "Its",
          alts: ["its"],
          tip: "Animal/thing → Its.",
          successTip: "ITS (possessive) ≠ IT'S (it is)! VERY common mistake! ITS = su (de cosa). IT'S = it is. ⚠️"
        },
        
        {
          type: "input",
          q: "Emma and students: 'We have a teacher. ___ teacher is nice.'",
          a: "Our",
          alts: ["our"],
          tip: "We → Our."
        },
        
        {
          type: "order",
          q: "Traduce: 'Su perro es grande.' (de ellos)",
          words: ["Their", "dog", "is", "big."],
          a: "Their dog is big.",
          extra: ["Our", "small"],
          tip: "'Their' = su (de ellos/ellas)."
        },
        
        {
          type: "match",
          q: "Match each group to possessive.",
          pairs: [
            { left: "You and I", right: "our" },
            { left: "Tom and Lisa", right: "their" },
            { left: "The country", right: "its" }
          ],
          a: "completed",
          tip: "Groups use different possessives.",
          successTip: "Countries/cities/companies = ITS! Spain has ITS culture. NOT 'his' or 'her'! 🇪🇸"
        },
        
        {
          type: "mc",
          q: "Emma: 'Spain is famous. ___ food is delicious.'",
          options: ["Its", "Her", "His"],
          a: "Its",
          tip: "Country (thing) → Its."
        },
        
        {
          type: "input",
          q: "Emma: 'My brothers are 30. ___ names are Tom and Bob.'",
          a: "Their",
          alts: ["their"],
          tip: "Brothers (they) → Their."
        },
        
        {
          type: "order",
          q: "Traduce: 'Nuestro profesor es amable.'",
          words: ["Our", "teacher", "is", "nice."],
          a: "Our teacher is nice.",
          extra: ["Their", "good"],
          tip: "'Our' = nuestro/a."
        }
      ]
    },

    // ===================================================================
    // SET 4: THIS vs THAT (A1+)
    // ===================================================================
    {
      id: "u5-set4",
      title: "Set 4: Near or Far?",
      context: `*Señalando Objetos - Clase*

Emma señala objetos: "This is my pen (cerca)."
"That is my bag (lejos)."
Los estudiantes practican: "This or That?"

*Pointing at Objects - Class*

Emma points at objects: "This is my pen (near)."
"That is my bag (far)."
Students practice: "This or That?"`,
      
      questions: [
        {
          type: "match",
          q: "Match distance to demonstrative.",
          pairs: [
            { left: "Near (in my hand)", right: "This" },
            { left: "Far (over there)", right: "That" },
            { left: "Near (on my desk)", right: "This" }
          ],
          a: "completed",
          tip: "Near = This, Far = That.",
          successTip: "Distance! THIS (cerca/aquí) 👆. THAT (lejos/allá) 👉. Like pointing! 📍"
        },
        
        {
          type: "mc",
          q: "Emma holds a pen: '___ is my pen.'",
          options: ["This", "That", "These"],
          a: "This",
          tip: "In hand (near) = This.",
          successTip: "THIS = in my hand/near me! Like 'este' in Spanish! ✋"
        },
        
        {
          type: "mc",
          q: "Emma points far: '___ is my house over there.'",
          options: ["That", "This", "Those"],
          a: "That",
          tip: "Far = That."
        },
        
        {
          type: "input",
          q: "Emma points far: 'Look at ___ car over there!'",
          a: "that",
          alts: ["That"],
          tip: "'Over there' = far = That."
        },
        
        {
          type: "input",
          q: "Emma holds book: '___ is my book.'",
          a: "This",
          alts: ["this"],
          tip: "Near = This.",
          successTip: "'This is my...' = presenting something near you! Common introduction! 📚"
        },
        
        {
          type: "order",
          q: "Traduce: 'Ese es tu coche.' (lejos)",
          words: ["That", "is", "your", "car."],
          a: "That is your car.",
          extra: ["This", "my"],
          tip: "'That' = ese/esa/eso (far)."
        },
        
        {
          type: "match",
          q: "Match sentence to demonstrative.",
          pairs: [
            { left: "This is", right: "my phone (here)" },
            { left: "That is", right: "my school (there)" },
            { left: "Is this", right: "your pen?" }
          ],
          a: "completed",
          tip: "This (here) vs That (there).",
          successTip: "'This is...' vs 'That is...' = super common structures! Practice them! 💬"
        },
        
        {
          type: "mc",
          q: "Student points far: 'Who is ___ man over there?'",
          options: ["that", "this", "these"],
          a: "that",
          tip: "Far person = that."
        },
        
        {
          type: "input",
          q: "Emma holds phone: 'Is ___ your phone?'",
          a: "this",
          alts: ["This"],
          tip: "Near question = this.",
          successTip: "'Is this your...?' = asking about something near! Very useful question! 📱"
        },
        
        {
          type: "order",
          q: "Traduce: '¿Quién es ese?'",
          words: ["Who", "is", "that?"],
          a: "Who is that?",
          extra: ["this", "What"],
          tip: "'Who is that?' = asking about person far."
        }
      ]
    },

    // ===================================================================
    // SET 5: COMBINING (A1+)
    // ===================================================================
    {
      id: "u5-set5",
      title: "Set 5: Family Introductions",
      context: `*Presentando Familia - Fotos*

Emma presenta a su familia con fotos.
"This is my mother. Her name is Mary."
"That is my father. His name is John."

*Introducing Family - Photos*

Emma introduces her family with photos.
"This is my mother. Her name is Mary."
"That is my father. His name is John."`,
      
      questions: [
        {
          type: "match",
          q: "Match introduction to completion.",
          pairs: [
            { left: "This is", right: "my mother (here)" },
            { left: "That is", right: "my father (there)" },
            { left: "Her name", right: "is Mary" }
          ],
          a: "completed",
          tip: "This/That + possessives.",
          successTip: "Combining! This/That + my/his/her. 'This is my mother. Her name is...' Perfect intro! 👨‍👩‍👧"
        },
        
        {
          type: "mc",
          q: "Emma points near: '___ is my brother.'",
          options: ["This", "That", "These"],
          a: "This",
          tip: "Near person = This."
        },
        
        {
          type: "mc",
          q: "Emma points far: '___ is my grandmother over there.'",
          options: ["That", "This", "Those"],
          a: "That",
          tip: "Far person = That."
        },
        
        {
          type: "input",
          q: "Emma: 'This is my sister. ___ name is Lisa.'",
          a: "Her",
          alts: ["her"],
          tip: "Sister (female) → Her."
        },
        
        {
          type: "input",
          q: "Emma: 'That is my uncle. ___ name is Bob.'",
          a: "His",
          alts: ["his"],
          tip: "Uncle (male) → His.",
          successTip: "Pattern! This is [family]. His/Her name is... Use it for introductions! 💬"
        },
        
        {
          type: "order",
          q: "Traduce: 'Esta es mi familia.'",
          words: ["This", "is", "my", "family."],
          a: "This is my family.",
          extra: ["That", "your"],
          tip: "'This is my family' = common phrase."
        },
        
        {
          type: "match",
          q: "Match family to relationship.",
          pairs: [
            { left: "My mom's dad", right: "Grandfather" },
            { left: "My dad's brother", right: "Uncle" },
            { left: "My aunt's child", right: "Cousin" }
          ],
          a: "completed",
          tip: "Family relationships (review)."
        },
        
        {
          type: "mc",
          q: "Emma: 'This is my cousin. ___ is 10 years old.'",
          options: ["He", "His", "Him"],
          a: "He",
          tip: "Subject pronoun 'He' (not possessive).",
          successTip: "HE (subject) vs HIS (possessive)! 'He is 10' ✅. 'His name is Tom' ✅. Different uses! 👦"
        },
        
        {
          type: "input",
          q: "Emma and family: 'We love ___ parents.'",
          a: "our",
          alts: ["Our"],
          tip: "We → our."
        },
        
        {
          type: "order",
          q: "Traduce: 'Esa es su hermana.' (de ella)",
          words: ["That", "is", "her", "sister."],
          a: "That is her sister.",
          extra: ["his", "brother"],
          tip: "'Her sister' = su hermana (de ella)."
        }
      ]
    },

    // ===================================================================
    // SET 6: PETS (A2-)
    // ===================================================================
    {
      id: "u5-set6",
      title: "Set 6: Our Pets",
      context: `*Hablando de Mascotas - Clase*

Los estudiantes hablan de mascotas.
"I have a dog. My dog is brown. His name is Max."
"She has a cat. Her cat is white. Its name is Luna."

*Talking About Pets - Class*

Students talk about pets.
"I have a dog. My dog is brown. His name is Max."
"She has a cat. Her cat is white. Its name is Luna."`,
      
      questions: [
        {
          type: "match",
          q: "Match owner to pet phrase.",
          pairs: [
            { left: "I have a dog", right: "My dog" },
            { left: "She has a cat", right: "Her cat" },
            { left: "He has a fish", right: "His fish" }
          ],
          a: "completed",
          tip: "Same possessives for pets.",
          successTip: "Pets use same possessives! My dog, his cat, their bird. Easy! 🐕🐱🐦"
        },
        
        {
          type: "mc",
          q: "Student: 'The dog has a ball. ___ ball is red.'",
          options: ["Its", "His", "Her"],
          a: "Its",
          tip: "Animal → Its.",
          successTip: "Animals = ITS (neutral)! Unless you say: My dog = he (male) or she (female). 🐕"
        },
        
        {
          type: "mc",
          q: "Emma and students: 'We have a bird. ___ bird is blue.'",
          options: ["Our", "Us", "We"],
          a: "Our",
          tip: "We → Our."
        },
        
        {
          type: "input",
          q: "Students: 'They have a rabbit. ___ rabbit is white.'",
          a: "Their",
          alts: ["their"],
          tip: "They → Their."
        },
        
        {
          type: "input",
          q: "Student asks: 'Do you have a dog? Is ___ dog big?'",
          a: "your",
          alts: ["Your"],
          tip: "You → your."
        },
        
        {
          type: "order",
          q: "Traduce: '¿Es este tu conejo?'",
          words: ["Is", "this", "your", "rabbit?"],
          a: "Is this your rabbit?",
          extra: ["that", "my"],
          tip: "'Is this your...?' = question."
        },
        
        {
          type: "match",
          q: "Match animal to home.",
          pairs: [
            { left: "Fish", right: "Water" },
            { left: "Bird", right: "Cage" },
            { left: "Dog", right: "House" }
          ],
          a: "completed",
          tip: "Animal homes (review)."
        },
        
        {
          type: "mc",
          q: "Student: 'My cat is white. ___ eyes are green.'",
          options: ["Its", "His", "Her"],
          a: "Its",
          tip: "Cat (animal) → Its."
        },
        
        {
          type: "input",
          q: "Student: 'She has a horse. ___ horse is brown.'",
          a: "Her",
          alts: ["her"],
          tip: "She → Her."
        },
        
        {
          type: "order",
          q: "Traduce: 'Esa es nuestra vaca.'",
          words: ["That", "is", "our", "cow."],
          a: "That is our cow.",
          extra: ["This", "their"],
          tip: "'Our cow' = nuestra vaca."
        }
      ]
    },

    // ===================================================================
    // SET 7: ERROR CORRECTION (A2-)
    // ===================================================================
    {
      id: "u5-set7",
      title: "Set 7: Common Mistakes",
      context: `*Corrigiendo Errores - Clase*

Emma corrige errores comunes de estudiantes.
"I name is Tom" → "My name is Tom"
"She dog is big" → "Her dog is big"

*Correcting Mistakes - Class*

Emma corrects common student errors.
"I name is Tom" → "My name is Tom"
"She dog is big" → "Her dog is big"`,
      
      questions: [
        {
          type: "mc",
          q: "ERROR: 'I name is Tom.' CORRECT:",
          options: ["My name is Tom", "I's name is Tom", "Me name is Tom"],
          a: "My name is Tom",
          tip: "Never 'I name' ❌. Always 'MY name' ✅.",
          successTip: "VERY common error! 'I name' ❌. 'My name' ✅. I = subject. MY = possessive! ⚠️"
        },
        
        {
          type: "mc",
          q: "ERROR: 'She dog is big.' CORRECT:",
          options: ["Her dog is big", "She's dog is big", "Hers dog is big"],
          a: "Her dog is big",
          tip: "She → Her before noun."
        },
        
        {
          type: "input",
          q: "ERROR: 'He car is red.' CORRECT: '___ car is red.'",
          a: "His",
          alts: ["his"],
          tip: "He → His before noun."
        },
        
        {
          type: "input",
          q: "ERROR: 'We house is small.' CORRECT: '___ house is small.'",
          a: "Our",
          alts: ["our"],
          tip: "We → Our before noun.",
          successTip: "Subject ≠ Possessive! We ≠ Our. They ≠ Their. You ≠ Your. Learn the difference! 📚"
        },
        
        {
          type: "match",
          q: "Match ERROR to CORRECT.",
          pairs: [
            { left: "You book", right: "Your book" },
            { left: "They house", right: "Their house" },
            { left: "It ball", right: "Its ball" }
          ],
          a: "completed",
          tip: "Add possessive adjectives."
        },
        
        {
          type: "order",
          q: "Traduce: '¿Es este su bolígrafo?' (de él)",
          words: ["Is", "this", "his", "pen?"],
          a: "Is this his pen?",
          extra: ["her", "that"],
          tip: "'His pen' = su bolígrafo (de él)."
        },
        
        {
          type: "mc",
          q: "ERROR: 'This are my parents.' CORRECT:",
          options: ["These are my parents", "This is my parents", "These is my parents"],
          a: "These are my parents",
          tip: "Parents (plural) = These are.",
          successTip: "THIS/THAT (singular) → is. THESE/THOSE (plural) → are. Grammar rule! ✅"
        },
        
        {
          type: "input",
          q: "ERROR: 'That are my father.' CORRECT: '___ is my father.'",
          a: "That",
          alts: ["that"],
          tip: "Father (singular) → That is."
        },
        
        {
          type: "order",
          q: "Traduce: 'Su nombre es Sarah.' (de ella)",
          words: ["Her", "name", "is", "Sarah."],
          a: "Her name is Sarah.",
          extra: ["His", "She"],
          tip: "'Her name' = su nombre (de ella)."
        },
        
        {
          type: "mc",
          q: "ERROR: 'You teacher is nice.' CORRECT:",
          options: ["Your teacher is nice", "You're teacher is nice", "Yours teacher is nice"],
          a: "Your teacher is nice",
          tip: "You → Your before noun.",
          successTip: "YOU (subject) vs YOUR (possessive)! 'You are nice' ✅. 'Your name is Tom' ✅. Different! 👤"
        }
      ]
    },

    // ===================================================================
    // SET 8: REVIEW PRACTICE (A2-)
    // ===================================================================
    {
      id: "u5-set8",
      title: "Set 8: Mixed Practice",
      context: `*Práctica General - Clase*

Emma mezcla todos los conceptos.
"My school. His car. Her cat. Our family. Their house."
Los estudiantes practican todo junto.

*Mixed Practice - Class*

Emma mixes all concepts.
"My school. His car. Her cat. Our family. Their house."
Students practice everything together.`,
      
      questions: [
        {
          type: "mc",
          q: "Emma: 'I am a teacher. ___ school is big.'",
          options: ["My", "I", "Me"],
          a: "My",
          tip: "I → My before noun."
        },
        
        {
          type: "mc",
          q: "Student: 'That is Tom. ___ car is new.'",
          options: ["His", "He", "Him"],
          a: "His",
          tip: "Tom (male) → His."
        },
        
        {
          type: "input",
          q: "Emma: 'This is my mother. ___ name is Mary.'",
          a: "Her",
          alts: ["her"],
          tip: "Mother (female) → Her."
        },
        
        {
          type: "match",
          q: "Match subject to possessive.",
          pairs: [
            { left: "I", right: "My" },
            { left: "She", right: "Her" },
            { left: "We", right: "Our" },
            { left: "It", right: "Its" }
          ],
          a: "completed",
          tip: "All possessive adjectives.",
          successTip: "Complete set! I→My, You→Your, He→His, She→Her, It→Its, We→Our, They→Their. 7 total! 📝"
        },
        
        {
          type: "order",
          q: "Traduce: '¿Es esa tu bolsa?'",
          words: ["Is", "that", "your", "bag?"],
          a: "Is that your bag?",
          extra: ["this", "my"],
          tip: "'That' (far) + 'your' (tu)."
        },
        
        {
          type: "input",
          q: "Emma and students: 'We are students. ___ teacher is nice.'",
          a: "Our",
          alts: ["our"],
          tip: "We → Our."
        },
        
        {
          type: "mc",
          q: "Emma holds pen: '___ is my pen.'",
          options: ["This", "That", "These"],
          a: "This",
          tip: "Near (in hand) = This."
        },
        
        {
          type: "mc",
          q: "Emma points far: 'Look at ___ school over there.'",
          options: ["that", "this", "those"],
          a: "that",
          tip: "Far (over there) = That."
        },
        
        {
          type: "order",
          q: "Traduce: 'Su casa es vieja.' (de ellos)",
          words: ["Their", "house", "is", "old."],
          a: "Their house is old.",
          extra: ["Our", "new"],
          tip: "'Their' = su (de ellos)."
        },
        
        {
          type: "mc",
          q: "Students: 'The cat has a toy. ___ toy is small.'",
          options: ["Its", "His", "Her"],
          a: "Its",
          tip: "Animal → Its.",
          successTip: "ITS for animals/things! The dog and ITS bone. The cat and ITS toy. Pattern! 🐕🐱"
        }
      ]
    },

    // ===================================================================
    // SET 9: CLASSROOM DIALOGUE (A2-)
    // ===================================================================
    {
      id: "u5-set9",
      title: "Set 9: Lost and Found",
      context: `*Objetos Perdidos - Clase*

Un estudiante pierde su bolígrafo.
"Is this your pen?" "No, it is not my pen."
"Is it his pen?" "Yes, it is his pen!"

*Lost and Found - Class*

A student loses their pen.
"Is this your pen?" "No, it is not my pen."
"Is it his pen?" "Yes, it is his pen!"`,
      
      questions: [
        {
          type: "order",
          q: "Traduce: '¿Es este tu teléfono?'",
          words: ["Is", "this", "your", "phone?"],
          a: "Is this your phone?",
          extra: ["that", "my"],
          tip: "'Is this your...?' = common question.",
          successTip: "'Is this your...?' = asking if something belongs to someone! Super useful! 📱"
        },
        
        {
          type: "mc",
          q: "Student answers: 'No, it is not ___ phone.'",
          options: ["my", "I", "me"],
          a: "my",
          tip: "Not my = not mine."
        },
        
        {
          type: "input",
          q: "Emma asks: 'Is it ___ phone?' (de él)",
          a: "his",
          alts: ["His"],
          tip: "De él → his."
        },
        
        {
          type: "mc",
          q: "Student: 'Yes, it is ___ phone!'",
          options: ["his", "he", "him"],
          a: "his",
          tip: "His phone = su teléfono (de él)."
        },
        
        {
          type: "match",
          q: "Match question to answer.",
          pairs: [
            { left: "Is this", right: "your pen?" },
            { left: "No, it is", right: "not my pen" },
            { left: "It is", right: "his pen" }
          ],
          a: "completed",
          tip: "Question → answer pattern.",
          successTip: "Dialogue pattern! Question: 'Is this your...?' Answer: 'Yes, it is' OR 'No, it is not'. 💬"
        },
        
        {
          type: "order",
          q: "Traduce: 'Sí, es mi bolígrafo.'",
          words: ["Yes,", "it", "is", "my", "pen."],
          a: "Yes, it is my pen.",
          extra: ["your", "his"],
          tip: "'It is my...' = confirmation."
        },
        
        {
          type: "mc",
          q: "Emma: 'Is this bag hers?' Student: 'No, it is ___.'",
          options: ["yours", "your", "you"],
          a: "yours",
          tip: "'Yours' = tuyo/a (possessive pronoun).",
          successTip: "Possessive pronouns! Mine, yours, his, hers, ours, theirs. Advanced! But useful for answers! 👜"
        },
        
        {
          type: "input",
          q: "Emma finds book: 'Is this ___ book?' (tu)",
          a: "your",
          alts: ["Your"],
          tip: "You → your."
        },
        
        {
          type: "mc",
          q: "Student: 'These are our books. They are ___.'",
          options: ["ours", "our", "we"],
          a: "ours",
          tip: "'Ours' = nuestros/as (possessive pronoun).",
          successTip: "OURS (possessive pronoun) = nuestro/a/os/as. 'These books are ours' = Estos libros son nuestros. 📚"
        },
        
        {
          type: "order",
          q: "Traduce: 'Estos son nuestros.'",
          words: ["These", "are", "ours."],
          a: "These are ours.",
          extra: ["This", "yours"],
          tip: "'Ours' = nuestros (pronoun)."
        }
      ]
    },

    // ===================================================================
    // SET 10: FINAL EXAM (A2-)
    // ===================================================================
    {
      id: "u5-set10",
      title: "Set 10: Final Test",
      context: `*Examen Final - Clase*

Emma da un examen final de possessives y demonstratives.
Los estudiantes usan todo: my, your, his, her, its, our, their + this, that.
"You are ready!" dice Emma.

*Final Exam - Class*

Emma gives a final exam on possessives and demonstratives.
Students use everything: my, your, his, her, its, our, their + this, that.
"You are ready!" says Emma.`,
      
      questions: [
        {
          type: "input",
          q: "Final test: 'They have a dog. ___ dog is black.'",
          a: "Their",
          alts: ["their"],
          tip: "They → Their."
        },
        
        {
          type: "mc",
          q: "Emma points far: '___ is my school over there.'",
          options: ["That", "This", "Those"],
          a: "That",
          tip: "Far (over there) = That."
        },
        
        {
          type: "order",
          q: "Traduce: 'Nuestro perro es muy amigable.'",
          words: ["Our", "dog", "is", "very", "nice."],
          a: "Our dog is very nice.",
          extra: ["Their", "big"],
          tip: "'Our dog' = nuestro perro."
        },
        
        {
          type: "input",
          q: "Student: 'I have a cat. ___ cat is white.'",
          a: "My",
          alts: ["my"],
          tip: "I → My."
        },
        
        {
          type: "match",
          q: "FINAL: Match all possessives.",
          pairs: [
            { left: "I", right: "My" },
            { left: "She", right: "Her" },
            { left: "We", right: "Our" },
            { left: "It", right: "Its" }
          ],
          a: "completed",
          tip: "All possessive adjectives.",
          successTip: "Perfect! You know all possessive adjectives! I→My, You→Your, He→His, She→Her, It→Its, We→Our, They→Their! 🎓"
        },
        
        {
          type: "mc",
          q: "Emma: 'The bird has a cage. ___ cage is blue.'",
          options: ["Its", "His", "Her"],
          a: "Its",
          tip: "Bird (animal) → Its."
        },
        
        {
          type: "order",
          q: "Traduce: 'Esta es mi familia.'",
          words: ["This", "is", "my", "family."],
          a: "This is my family.",
          extra: ["That", "your"],
          tip: "'This is' = esta es (near)."
        },
        
        {
          type: "mc",
          q: "Student: 'Spain is beautiful. ___ food is delicious.'",
          options: ["Its", "His", "Her"],
          a: "Its",
          tip: "Country (thing) → Its.",
          successTip: "Countries = ITS! Spain and ITS food. Mexico and ITS culture. NOT 'his' or 'her'! 🇪🇸"
        },
        
        {
          type: "input",
          q: "Emma asks: 'Is this book yours?' Student: 'Yes, it is ___.'",
          a: "mine",
          alts: ["Mine"],
          tip: "'Mine' = mío/a (possessive pronoun).",
          successTip: "Possessive pronouns! Mine, yours, his, hers, its, ours, theirs. Use them to answer! 📖"
        },
        
        {
          type: "mc",
          q: "FINAL QUESTION: 'These books are ___.' (de ellos)",
          options: ["theirs", "their", "them"],
          a: "theirs",
          tip: "'Theirs' = suyos (de ellos).",
          successTip: "🎉 UNIT 5 COMPLETE! You mastered possessive adjectives (my, your, his, her, its, our, their) and demonstratives (this, that)! Next: Unit 6! 🚀"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (A2)
  // ===================================================================
  readingSet: {
    id: "u5-reading",
    title: "Set 11: Reading Practice",
    category: "reading",
    
    questions: [
      {
        type: "passage",
        passage: `**Emma's Family**

Hi! My name is Emma. This is my family. My father's name is John. My mother's name is Mary. They are very nice.`
      },
      
      {
        type: "mc",
        q: "What is Emma's father's name?",
        options: ["John", "Mary", "Emma"],
        a: "John",
        tip: "Father = John."
      },
      
      {
        type: "mc",
        q: "What is Emma's mother's name?",
        options: ["Mary", "John", "Emma"],
        a: "Mary",
        tip: "Mother = Mary."
      },
      
      {
        type: "mc",
        q: "Whose family is this?",
        options: ["Emma's", "John's", "Mary's"],
        a: "Emma's",
        tip: "Emma presents HER family.",
        successTip: "Possessive 's! Emma's family = la familia de Emma. Reading comprehension! 👨‍👩‍👧"
      },
      
      {
        type: "passage",
        passage: `**Emma's Pets**

Emma has two pets. She has a dog and a cat. The dog's name is Max. Max is brown. The cat's name is Luna. Luna is white.`
      },
      
      {
        type: "mc",
        q: "How many pets does Emma have?",
        options: ["Two", "One", "Three"],
        a: "Two",
        tip: "She has two pets."
      },
      
      {
        type: "mc",
        q: "What is the dog's name?",
        options: ["Max", "Luna", "Emma"],
        a: "Max",
        tip: "Dog = Max."
      },
      
      {
        type: "mc",
        q: "What color is Luna?",
        options: ["White", "Brown", "Black"],
        a: "White",
        tip: "Luna (cat) is white."
      },
      
      {
        type: "passage",
        passage: `**Emma's School**

Emma is a teacher. Her school is in Madrid. The school is big. Its name is "London School". Emma loves her students.`
      },
      
      {
        type: "mc",
        q: "Where is Emma's school?",
        options: ["Madrid", "London", "Spain"],
        a: "Madrid",
        tip: "School is in Madrid."
      },
      
      {
        type: "mc",
        q: "What is the school's name?",
        options: ["London School", "Madrid School", "Emma School"],
        a: "London School",
        tip: "Its name = London School.",
        successTip: "ITS name! The school (thing) = ITS. Perfect possessive! 🏫"
      },
      
      {
        type: "mc",
        q: "What does Emma love?",
        options: ["Her students", "Her school", "Her pets"],
        a: "Her students",
        tip: "Emma loves her students.",
        successTip: "Perfect! You can read about possessives in context! Family, pets, school = all use possessives! 🎉"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (A2)
  // ===================================================================
  reviewSet: {
    id: "u5-review",
    title: "Set 12: Unit Review",
    category: "review",
    
    questions: [
      {
        type: "match",
        q: "Review: Match subject to possessive.",
        pairs: [
          { left: "I", right: "my" },
          { left: "You", right: "your" },
          { left: "He", right: "his" }
        ],
        a: "completed",
        tip: "Possessive adjectives."
      },
      
      {
        type: "mc",
        q: "Review: 'This is John. ___ car is red.'",
        options: ["His", "He", "Him"],
        a: "His",
        tip: "John (male) → His."
      },
      
      {
        type: "input",
        q: "Review: 'The dog has a toy. ___ toy is small.'",
        a: "Its",
        alts: ["its"],
        tip: "Animal → Its."
      },
      
      {
        type: "mc",
        q: "Review: 'We have a house. ___ house is big.'",
        options: ["Our", "Us", "We"],
        a: "Our",
        tip: "We → Our."
      },
      
      {
        type: "order",
        q: "Review: Traduce: 'Ese es su libro.' (de ella)",
        words: ["That", "is", "her", "book."],
        a: "That is her book.",
        extra: ["his", "This"],
        tip: "'Her book' = su libro (de ella)."
      },
      
      {
        type: "mc",
        q: "Review: (Near) '___ is my pen.'",
        options: ["This", "That", "These"],
        a: "This",
        tip: "Near = This."
      },
      
      {
        type: "mc",
        q: "Review: (Far) '___ is my house over there.'",
        options: ["That", "This", "Those"],
        a: "That",
        tip: "Far = That."
      },
      
      {
        type: "input",
        q: "Review: 'They have a cat. ___ cat is black.'",
        a: "Their",
        alts: ["their"],
        tip: "They → Their."
      },
      
      {
        type: "match",
        q: "Review: Match demonstrative to distance.",
        pairs: [
          { left: "This", right: "Near" },
          { left: "That", right: "Far" },
          { left: "These", right: "Near (plural)" }
        ],
        a: "completed",
        tip: "Distance = key."
      },
      
      {
        type: "mc",
        q: "Review: 'Is this yours?' 'Yes, it is ___.'",
        options: ["mine", "my", "I"],
        a: "mine",
        tip: "'Mine' = possessive pronoun.",
        successTip: "🎉 UNIT 5 REVIEW COMPLETE! You mastered possessive adjectives and demonstratives! Ready for Unit 6! 🚀"
      }
    ]
  }
};
